import React from "react";
//#region types
import { TabDialogContentType, DialogProps, SelectionDialogProps } from "models/propTypes/dialogPropTypes";
import { SelectionItem, SelectionKeyType } from "models/utilTypes/selectionTypes";
import { TranslationKeys } from "translations";
import { CommonEnums, StyleEnums } from "models/enums";
//#endregion
//#region components
import useFloatingMessages from "../messages/useFloatingMessages";
import AsyncResultListContainer from "../lists/asyncResultList/asyncResultListContainer";
import TabEvents from "../tabs/tabEvents";
import DialogLayout from "./_layout";
import TabTop from "../tabs/tabTop";
import TabPanel from "../tabs/tabPanel";
import { ErrorBoundary } from "components/error";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance } from "utils/helpers";

const SelectionDialog: React.FC<DialogProps> = (props) => {
  const [selectedItems, setSelectedItems] = React.useState<SelectionItem[]>([]);
  const selectionProps = props.children as SelectionDialogProps;
  const { enqueueMessage } = useFloatingMessages();

  const onItemSelect = (item: SelectionItem) => {
    if (selectionProps.multiSelection) {
      setSelectedItems([...selectedItems, item]);
    } else {
      setSelectedItems([item]);
      if (selectionProps.handleSubmit) {
        setTimeout(() => {
          props.closeDialog && props.closeDialog();
        }, 200);
        selectionProps.handleSubmit(item).then((res) => {
          if (!props.disbleSuccessMessages && selectionProps.submitSuccessMessage) {
            enqueueMessage(
              LocalizationHelperInstance.getFormattedMessage(selectionProps.submitSuccessMessage),
              StyleEnums.DisplayType.Success
            );
          }
          props.onSubmit && props.onSubmit(res);
        });
      } else {
        props.onSubmit && props.onSubmit(item);
        props.closeDialog && props.closeDialog();
      }
    }
  };

  const onItemRemove = (id: SelectionKeyType) => {
    setSelectedItems(selectedItems.filter((s) => s.id !== id));
  };

  const onEnterHandler = (resetTabIndex: () => void) => {
    resetTabIndex();
    if (props.onEnterHandler) props.onEnterHandler();
  };

  const onSubmitClicked = async () => {
    if (selectionProps.handleSubmit) return selectionProps.handleSubmit(selectedItems);
    else return selectedItems;
  };

  const getSuggestions = async () => {
    const suggestions = await selectionProps.getSuggestions();
    return selectedItems.concat(suggestions.filter((s) => !selectedItems.some((r) => r.id === s.id)));
  };

  const tabs: TabDialogContentType = [
    {
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Suggestions),
      content: (
        <AsyncResultListContainer
          parentId={0}
          onResultItemSelect={onItemSelect}
          getResultList={getSuggestions}
          selectedIds={selectedItems}
          excludeIds={selectionProps.excludeIds}
          onResultItemRemove={onItemRemove}
          getItemTemplate={selectionProps.geSelectiontItemTemplate}
        />
      ),
    },
    {
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.All),
      content: (
        <AsyncResultListContainer
          parentId={0}
          onResultItemSelect={onItemSelect}
          hasSearch
          getResultList={selectionProps.search}
          selectedIds={selectedItems}
          excludeIds={selectionProps.excludeIds}
          onResultItemRemove={onItemRemove}
          getItemTemplate={selectionProps.geSelectiontItemTemplate}
        />
      ),
    },
  ];

  const getTabDialogs = (): TabDialogContentType => {
    return selectionProps.tabs
      ? selectionProps.tabs.map((tab) => {
          return {
            title: tab.title,
            content: (
              <AsyncResultListContainer
                parentId={0}
                onResultItemSelect={onItemSelect}
                hasSearch={tab.hasSearch}
                searchInputPlaceholder={tab.searchInputPlaceholder}
                getResultList={tab.getResultList}
                selectedIds={selectedItems}
                excludeIds={selectionProps.excludeIds}
                onResultItemRemove={onItemRemove}
                getItemTemplate={selectionProps.geSelectiontItemTemplate}
              />
            ),
          };
        })
      : tabs;
  };

  return (
    <TabEvents
      render={(currentIndex, onTabChange, resetTabIndex) => (
        <DialogLayout
          {...props}
          header={
            <TabTop
              tabKey={"selection-tab"}
              value={currentIndex}
              onChange={onTabChange}
              tabs={getTabDialogs().map((item) => {
                return {
                  text: item.title,
                  onClickHandler: item.onTabChangeHandler,
                };
              })}
            />
          }
          class={cssExports.dialogFullHeight}
          onEnterHandler={onEnterHandler.bind(null, resetTabIndex)}
          actions={
            selectionProps.multiSelection
              ? [
                  {
                    text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Select),
                    onClick: onSubmitClicked,
                    type: StyleEnums.DisplayType.Info,
                    disabled: selectionProps.multiSelection && selectedItems.length === 0,
                  },
                ]
              : undefined
          }
        >
          <ErrorBoundary subComponentType={CommonEnums.SubComponentType.Dialog}>
            {getTabDialogs().map((item, index) => {
              return (
                <TabPanel key={index} value={currentIndex} index={index}>
                  {item.content}
                </TabPanel>
              );
            })}
          </ErrorBoundary>
        </DialogLayout>
      )}
    />
  );
};

export default SelectionDialog;
